import { useState } from 'react';
import { useAuth, type RegisterData } from '../context/AuthContext';
import { authApi } from '../api';
import { LogIn, UserPlus, KeyRound, Eye, EyeOff, GraduationCap, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

type Tab = 'login' | 'register' | 'forgot';

export default function Login() {
  const [tab, setTab] = useState<Tab>('login');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center text-white">
          <GraduationCap className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">升学规划助手</h1>
          <p className="text-blue-100 text-sm mt-1">智能规划，助力升学</p>
        </div>

        {/* Tab buttons */}
        {tab !== 'forgot' && (
          <div className="flex border-b">
            <button
              className={`flex-1 py-3 text-center font-medium transition-colors ${tab === 'login' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setTab('login')}
            >
              <LogIn className="w-4 h-4 inline mr-1" />登录
            </button>
            <button
              className={`flex-1 py-3 text-center font-medium transition-colors ${tab === 'register' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setTab('register')}
            >
              <UserPlus className="w-4 h-4 inline mr-1" />注册
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {tab === 'login' && <LoginForm onForgot={() => setTab('forgot')} />}
          {tab === 'register' && <RegisterForm />}
          {tab === 'forgot' && <ForgotPasswordForm onBack={() => setTab('login')} />}
        </div>
      </div>
    </div>
  );
}

function LoginForm({ onForgot }: { onForgot: () => void }) {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '登录失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
        <input
          type="text"
          value={username}
          onChange={e => setUsername(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          placeholder="请输入用户名"
          autoComplete="username"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
        <div className="relative">
          <input
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-10"
            placeholder="请输入密码"
            autoComplete="current-password"
            required
          />
          <button type="button" className="absolute right-3 top-2.5 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
        {loading ? '登录中...' : '登录'}
      </button>
      <div className="text-center">
        <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={onForgot}>
          <KeyRound className="w-3 h-3 inline mr-1" />忘记密码?
        </button>
      </div>
    </form>
  );
}

type EducationLevel = 'senior' | 'junior';

function RegisterForm() {
  const { register } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Username availability check
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const checkUsername = async (name: string) => {
    if (name.length < 2) { setUsernameStatus('idle'); return; }
    setUsernameStatus('checking');
    try {
      const res = await authApi.checkUsername(name);
      setUsernameStatus(res.data.available ? 'available' : 'taken');
    } catch {
      setUsernameStatus('idle');
    }
  };

  // Student info
  const [eduLevel, setEduLevel] = useState<EducationLevel>('senior');
  const [studentName, setStudentName] = useState('');
  const [school, setSchool] = useState('');
  const [grade, setGrade] = useState('高二');
  const [subjectType, setSubjectType] = useState('物理类');
  const [gradeTotal, setGradeTotal] = useState(750);
  const [targetLevel, setTargetLevel] = useState('985/211');

  const seniorGrades = ['高一', '高二', '高三'];
  const juniorGrades = ['初一', '初二', '初三'];

  const handleEduChange = (level: EducationLevel) => {
    setEduLevel(level);
    if (level === 'junior') {
      setGrade('初二');
      setSubjectType('');
      setGradeTotal(800);
      setTargetLevel('普高');
    } else {
      setGrade('高二');
      setSubjectType('物理类');
      setGradeTotal(750);
      setTargetLevel('985/211');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPwd) {
      setError('两次输入的密码不一致');
      return;
    }
    if (password.length < 6) {
      setError('密码长度不能少于6位');
      return;
    }
    if (usernameStatus === 'taken') {
      setError('用户名已被占用');
      return;
    }

    setLoading(true);
    try {
      const data: RegisterData = {
        username,
        password,
        displayName: displayName || studentName || username,
        studentInfo: studentName ? {
          name: studentName,
          school,
          grade,
          education_level: eduLevel,
          subject_type: subjectType,
          grade_total: gradeTotal,
          target_level: targetLevel,
        } : undefined,
      };
      await register(data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '注册失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}

      {/* 账号信息 */}
      <h3 className="font-medium text-gray-800 border-b pb-1">账号信息</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
        <div className="relative">
          <input
            type="text"
            value={username}
            onChange={e => { setUsername(e.target.value); checkUsername(e.target.value); }}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-8"
            placeholder="字母、数字、下划线，2-20位"
            autoComplete="username"
            required
          />
          <span className="absolute right-3 top-2.5">
            {usernameStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            {usernameStatus === 'available' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
            {usernameStatus === 'taken' && <XCircle className="w-4 h-4 text-red-500" />}
          </span>
        </div>
        {usernameStatus === 'taken' && <p className="text-xs text-red-500 mt-0.5">用户名已存在</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
        <div className="relative">
          <input
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none pr-10"
            placeholder="至少6位"
            autoComplete="new-password"
            required
          />
          <button type="button" className="absolute right-3 top-2.5 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">确认密码</label>
        <input
          type="password"
          value={confirmPwd}
          onChange={e => setConfirmPwd(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="再次输入密码"
          autoComplete="new-password"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
        <input
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="可选，默认使用学生姓名"
        />
      </div>

      {/* 学生信息 */}
      <h3 className="font-medium text-gray-800 border-b pb-1 mt-4">学生信息</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">学段</label>
        <div className="flex gap-2">
          <button type="button" className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${eduLevel === 'senior' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700'}`} onClick={() => handleEduChange('senior')}>高中</button>
          <button type="button" className={`flex-1 py-1.5 rounded-lg text-sm font-medium border ${eduLevel === 'junior' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700'}`} onClick={() => handleEduChange('junior')}>初中</button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">姓名</label>
        <input
          type="text"
          value={studentName}
          onChange={e => setStudentName(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="学生姓名"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">学校</label>
        <input
          type="text"
          value={school}
          onChange={e => setSchool(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="学校名称"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">年级</label>
          <select value={grade} onChange={e => setGrade(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
            {(eduLevel === 'senior' ? seniorGrades : juniorGrades).map(g => <option key={g}>{g}</option>)}
          </select>
        </div>
        {eduLevel === 'senior' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">选科</label>
            <select value={subjectType} onChange={e => setSubjectType(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option>物理类</option>
              <option>历史类</option>
            </select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">满分</label>
          <input type="number" value={gradeTotal} onChange={e => setGradeTotal(Number(e.target.value))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">目标</label>
          <select value={targetLevel} onChange={e => setTargetLevel(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
            {(eduLevel === 'senior'
              ? ['985', '985/211', '211', '一本']
              : ['一双外', '普高', '职高', '示范高中']
            ).map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
        {loading ? '注册中...' : '注册'}
      </button>
    </form>
  );
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [username, setUsername] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGetQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.getSecurityQuestion(username);
      setQuestion(res.data.question);
      setStep(2);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '查询失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPwd) {
      setError('两次输入的密码不一致');
      return;
    }
    if (newPassword.length < 6) {
      setError('密码长度不能少于6位');
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPassword(username, answer, newPassword);
      setStep(3);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '重置失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (step === 3) {
    return (
      <div className="text-center space-y-4">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
        <p className="text-lg font-medium text-gray-800">密码重置成功!</p>
        <p className="text-sm text-gray-500">请使用新密码登录</p>
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800 font-medium">返回登录</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800 text-sm">← 返回登录</button>
        <h3 className="font-medium text-gray-800">忘记密码</h3>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}

      {step === 1 && (
        <form onSubmit={handleGetQuestion} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="请输入你的用户名"
              required
            />
          </div>
          <button type="submit" disabled={loading} className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? '查询中...' : '下一步'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleReset} className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">安全问题</p>
            <p className="text-sm text-blue-700 mt-1">{question}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">你的答案</label>
            <input
              type="text"
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="至少6位"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
            <input
              type="password"
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>
          <button type="submit" disabled={loading} className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? '重置中...' : '重置密码'}
          </button>
        </form>
      )}
    </div>
  );
}
